import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dataMeet } from '../../data/data_meet';
import CreateInstanceForm from '../forms/CreateInstanceForm';
import SharingPanel from '../sharing/SharingPanel';
import ResetMeetingConfirm from './modals/ResetMeetingConfirm';
import CloneMeetingModal from './modals/CloneMeetingModal';
import SetCompleteConfirm from './modals/SetCompleteConfirm';
import AuthFunctions from '../../auth/auth_utils';
import './MeetingsTable.css';

function MeetingsTable({ statusFilter = null, instanceTypeFilter = null, title = "My Meetings", showCreateButton = false, createButtonPosition = "bottom" }) {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingInstance, setEditingInstance] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingInstance, setDeletingInstance] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });
  const [showSharingPanel, setShowSharingPanel] = useState(false);
  const [selectedMeetingForSharing, setSelectedMeetingForSharing] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resettingMeeting, setResettingMeeting] = useState(null);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloningMeeting, setCloningMeeting] = useState(null);
  const [showSetCompleteConfirm, setShowSetCompleteConfirm] = useState(false);
  const [completingMeeting, setCompletingMeeting] = useState(null);
  const [completing, setCompleting] = useState(false);

  // Get current user ID from JWT token
  const currentUserId = AuthFunctions.ExtractTokenUser();
  // console.log('🔍 Current user ID:', currentUserId);

  useEffect(() => {
    fetchMeetings();
  }, [statusFilter, instanceTypeFilter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.dropdown-toggle')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [activeDropdown]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatFullDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const truncateDescription = (description, maxLength = 60) => {
    if (!description) return '';
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength) + '...';
  };

  const getStateDisplay = (state) => {
    switch (state?.toLowerCase()) {
      case 'created':
        return 'Ready';
      case 'in-progress':
      case 'in-process':
        return 'In-Progress';
      case 'completed':
        return 'Completed';
      default:
        return state || 'Unknown';
    }
  };

  const getTypeDisplay = (type) => {
    switch (type?.toLowerCase()) {
      case 'liminal':
        return 'Liminal';
      case 'template':
        return 'On-Demand';
      case 'custom':
        return 'Custom';
      default:
        return type || 'Unknown';
    }
  };

  const getShortId = (meeting) => {
    return meeting.short_id || 
           meeting.instance?.short_id || 
           meeting.data?.short_id ||
           meeting.data?.instance?.short_id ||
           meeting.instance_id?.substring(0, 8) || 
           meeting.meetingId?.substring(0, 8) || 
           meeting._id?.substring(0, 8) || 
           meeting.id?.substring(0, 8) || 
           'N/A';
  };

  const handleStartMeeting = (meeting) => {
    // Navigate to the meeting preview page with the full meeting data in state
    navigate('/meeting-preview', { state: { meetingData: meeting } });
  };

  const handleJoinMeeting = (instanceId) => {
    // Navigate to the meeting page using the new URL-based viewer route
    navigate(`/meeting/${instanceId}`);
  };

  const handleViewMeeting = (instanceId) => {
    // For completed meetings, navigate with a special flag to indicate it's a completed meeting
    navigate('/meeting', { state: { instanceId, isCompleted: true } });
  };

  // Determine if this table is showing completed meetings
  const isCompletedMeetingsTable = statusFilter === 'completed';
  
  // Determine if this table is showing in-progress meetings (for join existing)
  const isInProgressMeetingsTable = statusFilter === 'in-process';
  
  // Debug logging
  console.log('🔍 MeetingsTable Debug:', {
    statusFilter,
    isInProgressMeetingsTable,
    meetingsCount: meetings.length,
    inProgressMeetings: meetings.filter(m => m.state?.toLowerCase() === 'in-progress' || m.state?.toLowerCase() === 'in-process')
  });

  // Determine if this table should show create button
  const shouldShowCreateButton = showCreateButton || (statusFilter === 'created' && 
    (instanceTypeFilter === 'liminal' || instanceTypeFilter === 'template'));
  


  const handleCreateInstance = () => {
    setShowCreateForm(true);
  };

  const handleInstanceCreated = (newInstance) => {
    // Refresh the meetings list
    fetchMeetings();
  };

  const handleInstanceUpdated = (updatedInstance) => {
    // Refresh the meetings list
    fetchMeetings();
  };

  const handleCloneSuccess = (clonedInstance) => {
    // Refresh the meetings list
    fetchMeetings();
  };

  const handleEditInstance = (meeting) => {
    setEditingInstance(meeting);
    setShowEditForm(true);
  };

  const handleDeleteInstance = (meeting) => {
    setDeletingInstance(meeting);
    setShowDeleteConfirm(true);
  };

  const handleSharing = (meeting) => {
    setSelectedMeetingForSharing(meeting);
    setShowSharingPanel(true);
    setActiveDropdown(null);
  };

  const handleResetMeeting = (meeting) => {
    setResettingMeeting(meeting);
    setShowResetConfirm(true);
    setActiveDropdown(null);
  };

  const handleCloneMeeting = (meeting) => {
    setCloningMeeting(meeting);
    setShowCloneModal(true);
    setActiveDropdown(null);
  };

  const handleSetComplete = (meeting) => {
    setCompletingMeeting(meeting);
    setShowSetCompleteConfirm(true);
    setActiveDropdown(null);
  };

  const confirmReset = async () => {
    if (!resettingMeeting) return;
    
    try {
      const response = await dataMeet.resetInstanceData(resettingMeeting.instance_id);
      if (response.success) {
        fetchMeetings();
      } else {
        setError('Failed to reset meeting data');
      }
    } catch (err) {
      setError('Error resetting meeting data: ' + err.message);
    } finally {
      setShowResetConfirm(false);
      setResettingMeeting(null);
    }
  };

  const confirmDelete = async () => {
    if (!deletingInstance) return;
    
    setDeleting(true);
    try {
      const response = await dataMeet.deleteInstance(deletingInstance.instance_id);
      if (response.success) {
        fetchMeetings();
      } else {
        setError('Failed to delete instance');
      }
    } catch (err) {
      setError('Error deleting instance: ' + err.message);
    } finally {
      setShowDeleteConfirm(false);
      setDeletingInstance(null);
      setDeleting(false);
    }
  };

  const confirmSetComplete = async () => {
    if (!completingMeeting) return;
    
    setCompleting(true);
    try {
      const response = await dataMeet.setInstanceComplete(completingMeeting.short_id);
      if (response.success) {
        fetchMeetings();
      } else {
        setError('Failed to set meeting as complete');
      }
    } catch (err) {
      setError('Error setting meeting as complete: ' + err.message);
    } finally {
      setShowSetCompleteConfirm(false);
      setCompletingMeeting(null);
      setCompleting(false);
    }
  };

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const response = await dataMeet.loadMeetings();
      if (response.success) {
        console.log('📋 Loaded meetings data:', response.instances);
        let filteredMeetings = response.instances || [];
        
        // Apply status filter with case-insensitive matching and variations
        if (statusFilter) {
          filteredMeetings = filteredMeetings.filter(meeting => {
            const meetingState = meeting.state?.toLowerCase();
            const filterState = statusFilter.toLowerCase();
            
            // Handle variations of "in-progress" state
            if (filterState === 'in-process') {
              return meetingState === 'in-process' || meetingState === 'in-progress';
            }
            
            return meetingState === filterState;
          });
        }
        
        // Apply instance type filter with case-insensitive matching
        if (instanceTypeFilter) {
          filteredMeetings = filteredMeetings.filter(meeting => 
            meeting.instance_type?.toLowerCase() === instanceTypeFilter.toLowerCase()
          );
        }
        
        setMeetings(filteredMeetings);
      } else {
        setError('Failed to load meetings');
      }
    } catch (err) {
      setError('Error loading meetings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="meetings-loading">Loading meetings...</div>;
  }

  if (error) {
    return <div className="meetings-error">Error: {error}</div>;
  }

  return (
    <div className="meetings-table-container">
      {/* Header with Title and Create Button */}
      {meetings.length > 0 && (
        <div className="table-header">
          <h2>{title}</h2>
          {shouldShowCreateButton && createButtonPosition === "top" && (
            <button 
              className="create-instance-btn"
              onClick={handleCreateInstance}
            >
              + Create New Meeting
            </button>
          )}
        </div>
      )}

      {meetings.length === 0 ? (
        <div className="empty-state-container">
          <div className="empty-state-content">
            {isInProgressMeetingsTable ? (
              <>
                <div className="empty-state-icon">
                  <i className="ti ti-users"></i>
                </div>
                <h3>No Meetings in Progress</h3>
                <p>There are currently no meetings in progress for you to join.</p>
              </>
            ) : isCompletedMeetingsTable ? (
              <>
                <h3>No Completed Meetings</h3>
                <p>You don't have any completed meetings to view yet.</p>
              </>
            ) : (
              <>
                <h3>Create Your First Meeting</h3>
                <p>Get started by creating a new meeting to begin your AI-powered coaching session.</p>
                {shouldShowCreateButton && (
                  <button 
                    className="create-instance-btn"
                    onClick={handleCreateInstance}
                  >
                    + Create New Meeting
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <>
          <table className="meetings-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Description</th>
                <th>State</th>
                <th>Level</th>
                <th>Created Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {meetings.map((meeting) => (
                <tr key={meeting.instance_id}>
                  <td className="meeting-id">{getShortId(meeting)}</td>
                  <td className="meeting-name">{meeting.name || 'Untitled Meeting'}</td>
                  <td className="meeting-description" title={meeting.description}>
                    {truncateDescription(meeting.description, 25)}
                  </td>
                  <td>
                    <span className={`state-badge state-${meeting.state?.toLowerCase()}`}>
                      {getStateDisplay(meeting.state)}
                    </span>
                  </td>
                  <td>
                    <span className={`level-badge level-${(meeting.level_name || 'standard').toLowerCase()}`}>
                      {meeting.level_name || 'Standard'}
                    </span>
                  </td>
                  <td className="meeting-date" title={formatFullDateTime(meeting.created_at)}>{formatDate(meeting.created_at)}</td>
                  <td>
                  <div className="action-buttons">
                    {isCompletedMeetingsTable ? (
                      <>
                        <button
                          className="action-btn view-btn"
                          onClick={() => handleViewMeeting(meeting.instance_id)}
                        >
                          View
                        </button>
                        <div className="dropdown-menu">
                          <button
                            className="dropdown-toggle"
                            onClick={(e) => {
                              e.stopPropagation();
                              const button = e.target;
                              const rect = button.getBoundingClientRect();
                              
                              if (activeDropdown === meeting.instance_id) {
                                setActiveDropdown(null);
                              } else {
                                setActiveDropdown(meeting.instance_id);
                                setDropdownPosition({
                                  x: rect.right - 140, // 140px is dropdown width
                                  y: rect.bottom + 4
                                });
                              }
                            }}
                          >
                            ⋯
                          </button>
                        </div>
                      </>
                    ) : isInProgressMeetingsTable ? (
                      <>
                        <button
                          className="action-btn join-btn"
                          onClick={() => handleJoinMeeting(meeting.instance_id)}
                        >
                          Join
                        </button>
                        {(() => {
                          // Only show dropdown menu if user is the owner
                          return (currentUserId === meeting.created_by || currentUserId === meeting.user_id || currentUserId === meeting.owner) ? (
                            <div className="dropdown-menu">
                              <button
                                className="dropdown-toggle"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const button = e.target;
                                  const rect = button.getBoundingClientRect();
                                  
                                  if (activeDropdown === meeting.instance_id) {
                                    setActiveDropdown(null);
                                  } else {
                                    setActiveDropdown(meeting.instance_id);
                                    setDropdownPosition({
                                      x: rect.right - 140, // 140px is dropdown width
                                      y: rect.bottom + 4
                                    });
                                  }
                                }}
                              >
                                ⋯
                              </button>
                            </div>
                          ) : null;
                        })()}
                      </>
                    ) : (
                      <>
                        <button
                          className="action-btn start-btn"
                          onClick={() => handleStartMeeting(meeting)}
                        >
                          Start
                        </button>
                        <div className="dropdown-menu">
                          <button
                            className="dropdown-toggle"
                            onClick={(e) => {
                              e.stopPropagation();
                              const button = e.target;
                              const rect = button.getBoundingClientRect();
                              
                              if (activeDropdown === meeting.instance_id) {
                                setActiveDropdown(null);
                              } else {
                                setActiveDropdown(meeting.instance_id);
                                setDropdownPosition({
                                  x: rect.right - 140, // 140px is dropdown width
                                  y: rect.bottom + 4
                                });
                              }
                            }}
                          >
                            ⋯
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {shouldShowCreateButton && createButtonPosition === "bottom" && (
            <div className="create-button-container">
              <button 
                className="create-instance-btn"
                onClick={handleCreateInstance}
              >
                + Create New Meeting
              </button>
            </div>
          )}
        </>
      )}
      
      <CreateInstanceForm
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onInstanceCreated={handleInstanceCreated}
        instanceType={instanceTypeFilter || 'template'}
        title="Create New Meeting"
      />
      
      <CreateInstanceForm
        isOpen={showEditForm}
        onClose={() => {
          setShowEditForm(false);
          setEditingInstance(null);
        }}
        onInstanceUpdated={handleInstanceUpdated}
        instanceType={instanceTypeFilter || 'template'}
        title="Edit Meeting"
        editMode={true}
        editData={editingInstance}
      />
      
      {activeDropdown && (
        <div 
          className="floating-dropdown"
          style={{
            position: 'fixed',
            left: dropdownPosition.x,
            top: dropdownPosition.y,
            zIndex: 10000
          }}
        >
          <div className="dropdown-content">
            {isCompletedMeetingsTable ? (
              // Completed meetings menu
              <>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    const meeting = meetings.find(m => m.instance_id === activeDropdown);
                    if (meeting) {
                      handleCloneMeeting(meeting);
                    }
                    setActiveDropdown(null);
                  }}
                >
                  Clone Meeting
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    const meeting = meetings.find(m => m.instance_id === activeDropdown);
                    if (meeting) {
                      handleSharing(meeting);
                    }
                    setActiveDropdown(null);
                  }}
                >
                  Sharing
                </button>
                <button
                  className="dropdown-item reset"
                  onClick={() => {
                    const meeting = meetings.find(m => m.instance_id === activeDropdown);
                    if (meeting) {
                      handleResetMeeting(meeting);
                    }
                    setActiveDropdown(null);
                  }}
                >
                  Reset Meeting Data
                </button>
                {(() => {
                  const meeting = meetings.find(m => m.instance_id === activeDropdown);
                  return meeting && (currentUserId === meeting.created_by || currentUserId === meeting.user_id || currentUserId === meeting.owner) ? (
                    <button
                      className="dropdown-item delete"
                      onClick={() => {
                        if (meeting) {
                          handleDeleteInstance(meeting);
                        }
                        setActiveDropdown(null);
                      }}
                    >
                      Delete Meeting
                    </button>
                  ) : null;
                })()}
              </>
            ) : isInProgressMeetingsTable ? (
              // In-progress meetings menu
              <>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    const meeting = meetings.find(m => m.instance_id === activeDropdown);
                    if (meeting) {
                      handleSharing(meeting);
                    }
                    setActiveDropdown(null);
                  }}
                >
                  Sharing
                </button>
                <button
                  className="dropdown-item complete"
                  onClick={() => {
                    const meeting = meetings.find(m => m.instance_id === activeDropdown);
                    if (meeting) {
                      handleSetComplete(meeting);
                    }
                    setActiveDropdown(null);
                  }}
                >
                  Set Meeting Complete
                </button>
              </>
            ) : (
              // Created meetings menu
              <>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    const meeting = meetings.find(m => m.instance_id === activeDropdown);
                    if (meeting) {
                      handleEditInstance(meeting);
                    }
                    setActiveDropdown(null);
                  }}
                >
                  Edit
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    const meeting = meetings.find(m => m.instance_id === activeDropdown);
                    if (meeting) {
                      handleSharing(meeting);
                    }
                    setActiveDropdown(null);
                  }}
                >
                  Sharing
                </button>
                <button
                  className="dropdown-item delete"
                  onClick={() => {
                    const meeting = meetings.find(m => m.instance_id === activeDropdown);
                    if (meeting) {
                      handleDeleteInstance(meeting);
                    }
                    setActiveDropdown(null);
                  }}
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      )}
      
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-confirmation-modal">
            <div className="modal-header">
              <h3>Delete Meeting</h3>
            </div>
            <div className="modal-content">
              <p>
                Are you sure you want to delete the meeting "<strong>{deletingInstance?.name}</strong>"?
              </p>
              <p className="warning-text">
                This action cannot be undone. The meeting and all associated data will be permanently removed.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingInstance(null);
                }} 
                className="cancel-button"
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete} 
                className="delete-confirm-button"
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <i className="ti ti-loader ti-spin"></i>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="ti ti-trash"></i>
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <SharingPanel
        isOpen={showSharingPanel}
        onClose={() => {
          setShowSharingPanel(false);
          setSelectedMeetingForSharing(null);
        }}
        instanceId={selectedMeetingForSharing?.instance_id}
        meetingName={selectedMeetingForSharing?.name}
        meetingData={selectedMeetingForSharing}
      />
      
      <ResetMeetingConfirm
        isOpen={showResetConfirm}
        onClose={() => {
          setShowResetConfirm(false);
          setResettingMeeting(null);
        }}
        onConfirm={confirmReset}
        meetingName={resettingMeeting?.name}
      />
      
      <CloneMeetingModal
        isOpen={showCloneModal}
        onClose={() => {
          setShowCloneModal(false);
          setCloningMeeting(null);
        }}
        onCloneSuccess={handleCloneSuccess}
        meetingData={cloningMeeting}
      />
      
      <SetCompleteConfirm
        isOpen={showSetCompleteConfirm}
        onClose={() => {
          setShowSetCompleteConfirm(false);
          setCompletingMeeting(null);
        }}
        onConfirm={confirmSetComplete}
        meetingName={completingMeeting?.name}
      />
    </div>
  );
}

export default MeetingsTable; 